/**
 * The browser half of an import's upload: hash, create, PUT, confirm.
 *
 * An effect from end to end, so no static render reaches it; U79's flow drives it through
 * the file chooser.
 */
import type { ImportSource } from "@packagepage/shared";

import { api } from "./procedures.js";
import { sha256Hex } from "./file-hash.js";
import { putToPresignedUrl } from "./upload.js";

export type UploadPhase = "hashing" | "creating" | "uploading" | "confirming";

/** The upload sources `createImportSchema` admits; null for any other file. */
export function importSourceOf(
  filename: string,
): Extract<ImportSource, "csv" | "xlsx"> | null {
  const extension = /\.([A-Za-z0-9]+)$/.exec(filename)?.[1]?.toLowerCase();
  return extension === "csv" || extension === "xlsx" ? extension : null;
}

/** Resolves with the import's id once the API has the file's size. Rejects with the door's own refusal. */
export async function uploadImportFile(
  file: File,
  onPhase: (phase: UploadPhase) => void,
): Promise<string> {
  const source = importSourceOf(file.name);
  if (source === null) throw new Error("Only .csv and .xlsx files can be imported.");

  onPhase("hashing");
  const contentSha256 = await sha256Hex(file);

  onPhase("creating");
  const created = await api.createImport({
    filename: file.name,
    bytes: file.size,
    contentSha256,
    idempotencyKey: crypto.randomUUID(),
    source,
  });

  if (created.upload !== null) {
    onPhase("uploading");
    await putToPresignedUrl(created.upload, file);
  }

  onPhase("confirming");
  await api.markImportUploaded(created.import.id);
  return created.import.id;
}
